import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { Box, Flex, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import { PlatformEnum } from '@fluenceur/types';
import { SiInstagram, SiTiktok } from '@icons-pack/react-simple-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PlatformFilterProps {
  value: string | null;
  onChange: (platform: string | null) => void;
  initialValue?: string | null;
}

const platformOptions = [
  {
    value: PlatformEnum.Instagram,
    translationKey: 'filters.platform.instagram',
    Icon: SiInstagram,
  },
  { value: PlatformEnum.TikTok, translationKey: 'filters.platform.tiktok', Icon: SiTiktok },
] as const;

export const PlatformFilter: React.FC<PlatformFilterProps> = ({
  value,
  onChange,
  initialValue = null,
}) => {
  const { t } = useTranslation();
  const [tempPlatform, setTempPlatform] = useState<string | null>(initialValue);

  useEffect(() => {
    setTempPlatform(value);
  }, [value]);

  const handleApply = () => {
    onChange(tempPlatform);
  };

  const handleClear = () => {
    setTempPlatform('');
    onChange(null);
  };

  const handlePlatformSelect = (platform: string) => {
    setTempPlatform(platform);
  };

  const isFilterActive = !!value;
  const selectedOption = platformOptions.find((option) => option.value === value);

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('filters.platform.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>
            <Flex align="center" gap={1.5}>
              {selectedOption && (
                <selectedOption.Icon size={12} color="var(--chakra-colors-dark)" />
              )}
              {selectedOption
                ? t(selectedOption.translationKey)
                : t('filters.platform.allPlatforms')}
            </Flex>
          </FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="200px">
            <FilterButtonMenuHeader title={t('filters.platform.label')} onClearAll={handleClear} />
            <Box p={3}>
              <VStack gap={0.5} align="stretch">
                {platformOptions.map((option) => (
                  <FilterMenuItem
                    key={option.value}
                    onClick={() => handlePlatformSelect(option.value)}
                    isSelected={tempPlatform === option.value}
                  >
                    <Flex align="center" gap={2}>
                      <option.Icon size={16} color="var(--chakra-colors-dark)" />
                      <Text fontSize="sm">{t(option.translationKey)}</Text>
                    </Flex>
                  </FilterMenuItem>
                ))}
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
